/**
 * The LT (Luby transform) code of NetID streams (SPEC.md §4): which source
 * blocks a frame's symbol XORs together is a pure function of (seed, k), so
 * a frame only has to carry its seed.
 */

/** mulberry32: a tiny 32-bit PRNG; returns floats in [0, 1). */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const cdfCache = new Map<number, Float64Array>();

/** Robust soliton distribution (c = 0.1, δ = 0.5), as a CDF over degrees 1..k. */
export function robustSolitonCdf(k: number): Float64Array {
  const hit = cdfCache.get(k);
  if (hit) return hit;
  const c = 0.1;
  const delta = 0.5;
  const R = c * Math.log(k / delta) * Math.sqrt(k);
  const spike = Math.max(1, Math.min(k, Math.floor(k / R)));
  const p = new Float64Array(k + 1);
  for (let d = 1; d <= k; d++) {
    const rho = d === 1 ? 1 / k : 1 / (d * (d - 1));
    let tau = 0;
    if (d < spike) tau = R / (d * k);
    else if (d === spike) tau = (R * Math.log(R / delta)) / k;
    p[d] = rho + Math.max(0, tau);
  }
  const cdf = new Float64Array(k + 1);
  let sum = 0;
  for (let d = 1; d <= k; d++) sum += p[d];
  let acc = 0;
  for (let d = 1; d <= k; d++) {
    acc += p[d] / sum;
    cdf[d] = acc;
  }
  cdf[k] = 1;
  if (cdfCache.size > 64) cdfCache.clear();
  cdfCache.set(k, cdf);
  return cdf;
}

/**
 * The encoder's choice of degree (robust soliton). Only the encoder runs
 * this: the degree travels in the frame header, because Math.log and friends
 * are not bit-exact across JS engines and a decoder must never disagree.
 */
export function sampleDegree(seed: number, k: number): number {
  if (k === 1) return 1;
  const u = mulberry32(seed ^ 0x9e3779b9)();
  const cdf = robustSolitonCdf(k);
  let degree = 1;
  while (degree < k && cdf[degree] < u) degree++;
  return degree;
}

/**
 * The sorted, distinct source block indices a frame combines: a partial
 * Fisher–Yates shuffle of 0..k-1 driven by mulberry32(seed). Integer-exact,
 * so every engine picks the same blocks.
 */
export function blocksForSeed(seed: number, k: number, degree: number): number[] {
  if (degree < 1 || degree > k) throw new Error(`degree ${degree} out of range for k=${k}`);
  const rnd = mulberry32(seed);
  const pool = Array.from({ length: k }, (_, i) => i);
  for (let i = 0; i < degree; i++) {
    const j = i + Math.floor(rnd() * (k - i));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  return pool.slice(0, degree).sort((a, b) => a - b);
}
